import "zod/compile";
import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  AgentSopSchema,
  AgentSopStepSchema,
  RemoteDesktopLoginConfigSchema,
  SopOutputsSchema,
  SopParamSchema,
  SopSessionRulesSchema,
  type AgentSopDto,
} from "./agent.contract";
import { parseVersionedBundle } from "./shared/parseVersionedBundle";

/**
 * The portable JSON form of a procedure (SOP) — what "Export as JSON" writes and
 * "Import from JSON" reads. Shared by the frontend (build/parse) and the backend
 * (validate + persist) so a file exported from one org imports into another
 * without a translation layer.
 *
 * Deliberately NOT in the file:
 * - database row ids (`id`, `versionId`, …) — import always creates a NEW procedure;
 * - derived display fields (`refName`, `branchStepId`, …) — re-resolved on read;
 * - `readOnlyAttested` — a human attestation about one org's real portal, not a property
 *   of the steps, so a copy must be re-attested;
 * - `builtinKey` / `builtinVersion` — a copy is hand-managed, not system-managed.
 *
 * Bump `SOP_BUNDLE_FORMAT_VERSION` only for a change an older reader cannot tolerate.
 */
export const SOP_BUNDLE_FORMAT_VERSION = 1 as const;

/** One step as it appears in a bundle: the persisted row minus ids and derived fields.
 * Order is the array order — `ordinal` is re-assigned on import. */
export const SopBundleStepSchema = AgentSopStepSchema.omit({
  id: true,
  ordinal: true,
  refName: true,
  branchStepId: true,
  branchPathId: true,
  branchPathLabel: true,
}).extend({
  stepId: z.string().trim().min(1),
});

export type SopBundleStep = z.infer<typeof SopBundleStepSchema>;

/** Lineage-level settings — everything on the SOP row that a copy should carry. The three
 * `*Id` fields are org-scoped soft references; import keeps them only when they resolve in the
 * target organization (see `importSopFromBundle`). */
export const SopBundleProcedureSchema = z.object({
  name: z.string().trim().min(1).max(255),
  title: z.string().trim().min(1).max(255),
  bodyMarkdown: z.string().max(200_000).default(""),
  sessionRules: SopSessionRulesSchema.default([]),
  parameters: z.array(SopParamSchema).default([]),
  /** Declared outputs (values a replay reports back). Absent in older bundles. */
  outputs: SopOutputsSchema.default([]),
  siteKey: z.string().max(255).nullish(),
  taskKey: z.string().max(255).nullish(),
  displayWidth: z.number().int().positive().nullish(),
  displayHeight: z.number().int().positive().nullish(),
  startUrl: z.string().url().nullish(),
  loginCredentialId: z.string().uuid().nullish(),
  fallbackLoginSopId: z.string().uuid().nullish(),
  remoteDesktopLogin: RemoteDesktopLoginConfigSchema.nullish(),
  visualOnly: z.boolean().default(false),
});

/** Provenance, for humans reading a diff. Never used to resolve anything. */
export const SopBundleSourceSchema = z.object({
  organizationId: z.string().optional(),
  sopId: z.string().optional(),
  version: z.number().optional(),
  exportedAt: z.string().optional(),
});

/** `stepId` is a step's identity within the procedure — branch paths bind by it, diffs and run
 * evidence key on it — so a file that repeats one has no single step to mean. */
function duplicateStepIds(steps: readonly { stepId: string }[]): string[] {
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const { stepId } of steps) {
    if (seen.has(stepId)) duplicates.add(stepId);
    seen.add(stepId);
  }
  return [...duplicates];
}

export const SopBundleSchema = z.object({
  formatVersion: z.literal(SOP_BUNDLE_FORMAT_VERSION),
  source: SopBundleSourceSchema.optional(),
  procedure: SopBundleProcedureSchema,
  steps: z
    .array(SopBundleStepSchema)
    .min(1)
    .superRefine((steps, ctx) => {
      const duplicates = duplicateStepIds(steps);
      if (duplicates.length > 0) {
        ctx.addIssue({
          code: "custom",
          message: `duplicate stepId: ${duplicates.join(", ")}`,
        });
      }
    }),
});

export type SopBundle = z.infer<typeof SopBundleSchema>;

/** Project a stored SOP (any version view) onto the portable shape. Pure; the frontend calls it
 * for Export, so it must never need anything a `getSop` response doesn't already carry. */
export function buildSopBundle(
  sop: AgentSopDto,
  exportedAt: Date = new Date(),
): SopBundle {
  return {
    formatVersion: SOP_BUNDLE_FORMAT_VERSION,
    source: {
      organizationId: sop.organizationId,
      sopId: sop.id,
      version: sop.version,
      exportedAt: exportedAt.toISOString(),
    },
    procedure: {
      name: sop.name,
      title: sop.title,
      bodyMarkdown: sop.bodyMarkdown,
      sessionRules: sop.sessionRules,
      parameters: sop.parameters,
      outputs: sop.outputs,
      siteKey: sop.siteKey,
      taskKey: sop.taskKey,
      displayWidth: sop.displayWidth,
      displayHeight: sop.displayHeight,
      startUrl: sop.startUrl,
      loginCredentialId: sop.loginCredentialId,
      fallbackLoginSopId: sop.fallbackLoginSopId,
      remoteDesktopLogin: sop.remoteDesktopLogin,
      visualOnly: sop.visualOnly,
    },
    steps: [...sop.steps]
      .sort((a, b) => a.ordinal - b.ordinal)
      .map((step) => ({
        stepId: step.stepId,
        kind: step.kind,
        name: step.name,
        description: step.description,
        constraints: step.constraints,
        surface: step.surface,
        provenance: step.provenance,
        authorship: step.authorship,
        risk: step.risk,
        target: step.target,
        action: step.action,
        verify: step.verify,
        refSopId: step.refSopId,
        refVersion: step.refVersion,
        paramBindings: step.paramBindings,
        branch: step.branch,
        phase: step.phase,
        sensitive: step.sensitive,
        bestEffort: step.bestEffort,
      })),
  };
}

/** Parse untrusted JSON into a bundle, or a flat list of readable errors. */
export function parseSopBundle(
  raw: unknown,
): { ok: true; bundle: SopBundle } | { ok: false; errors: string[] } {
  return parseVersionedBundle(raw, {
    schema: SopBundleSchema,
    formatVersion: SOP_BUNDLE_FORMAT_VERSION,
    label: "procedure",
  });
}

export const ImportSopBundleInputSchema = z.object({
  organizationId: z.string().uuid(),
  bundle: SopBundleSchema,
  /** Optional slug override; derived from `bundle.procedure.name` when omitted. Always
   * de-duplicated per org so an import creates a new procedure rather than superseding one. */
  name: z.string().trim().min(1).max(255).nullish(),
});

export type ImportSopBundleInput = z.infer<typeof ImportSopBundleInputSchema>;

/** Every org-scoped soft reference a bundle can carry, by its path in the bundle. */
export const SopBundleDroppedReferenceSchema = z.enum([
  "loginCredentialId",
  "fallbackLoginSopId",
  "remoteDesktopLogin.vmCredentialSetId",
  "remoteDesktopLogin.otpTargetId",
]);

export type SopBundleDroppedReference = z.infer<
  typeof SopBundleDroppedReferenceSchema
>;

export const ImportSopBundleResponseSchema = z.object({
  sop: AgentSopSchema,
  /** Org-scoped references the file carried that don't exist in this organization and were
   * therefore dropped (e.g. "loginCredentialId"). Empty on a same-org round trip. */
  droppedReferences: z.array(SopBundleDroppedReferenceSchema),
});

export type ImportSopBundleResponse = z.infer<
  typeof ImportSopBundleResponseSchema
>;

export const ImportSopBundleDraftInputSchema = z.object({
  organizationId: z.string().uuid(),
  /** The procedure the bundle becomes a draft of — it keeps its id, name, history and login
   * config, whatever `bundle.source` and `bundle.procedure.name` say. */
  sopId: z.string().uuid(),
  bundle: SopBundleSchema,
});

export type ImportSopBundleDraftInput = z.infer<
  typeof ImportSopBundleDraftInputSchema
>;

export const ImportSopBundleDraftResponseSchema = z.object({
  /** The new draft's view — `version` is the draft's number. */
  sop: AgentSopSchema,
  versionId: z.string().uuid(),
});

export type ImportSopBundleDraftResponse = z.infer<
  typeof ImportSopBundleDraftResponseSchema
>;

export const sopBundleContracts = {
  // Create a NEW procedure from an exported `.sop.json` bundle (steps are persisted verbatim;
  // composed `sop_ref` steps must resolve to published procedures in this organization).
  importSopBundle: oc
    .input(ImportSopBundleInputSchema)
    .output(ImportSopBundleResponseSchema)
    .route({
      method: "POST",
      summary: "Import a procedure from JSON",
      description:
        "Creates a new agent procedure (SOP) from an exported JSON file, keeping its steps and settings; lists credential and login references dropped because they don't exist here.",
    }),
  // Write an exported bundle as the caller's new unpublished DRAFT of an existing procedure, so
  // its id, history and every pin on it survive. Publishing stays the page's separate action.
  importSopBundleDraft: oc
    .input(ImportSopBundleDraftInputSchema)
    .output(ImportSopBundleDraftResponseSchema)
    .route({
      method: "POST",
      summary: "Import a procedure draft from JSON",
      description:
        "Creates an unpublished draft version of an existing agent procedure (SOP) from an exported JSON file: steps, instructions, parameters, outputs and session rules come from the file; the procedure's name, title and login configuration are kept.",
    }),
};
